import { Component, Input, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ChartConfiguration, ChartOptions, ChartType } from "chart.js";
import { consts, months, monthsNumeric } from "../../../../consts";
import { MonthModel } from "../../../../shared/components/table/month.model";
import { ActualSelected, Utils } from "../../../../shared/utils";
import { ResumeService } from "../../resume.service";
import { ResumeV2Model } from "../../resumeV2.model";
import { UntypedFormControl } from "@angular/forms";
import { BalanceDialogComponent } from "../../balance/balance-dialog.component";
import { ResumeDialogComponent } from "../../resume-dialog.component";
import { chartPalette, doughnutOptionsBase, euro } from "../chart-theme";

@Component({
    selector: 'chart-resume-earning',
    templateUrl: './chart-resume-earning.component.html',
    styleUrls: ['../../resume.component.css'],
    standalone: false
})
export class ChartResumeEarningComponent implements OnInit {
  @Input() showGlobal: boolean = false;
  @Input() dataV2: ResumeV2Model = new ResumeV2Model();

  totalIncomesPaid: number = 0;
  totalIncomesUnPaid: number = 0;

  monthControl = new UntypedFormControl(monthsNumeric);
  monthsSelected: string[];
  monthList: MonthModel[] = [];
  allMonthsSelected = false;

  public chartType: ChartType = 'doughnut';
  public chartData: ChartConfiguration<'doughnut'>['data'] = {
    labels: ['No cobrado', 'Cobrado'],
    datasets: [{
      data: [0, 0],
      backgroundColor: [chartPalette.warning, chartPalette.good],
      hoverBackgroundColor: [chartPalette.warning, chartPalette.good],
      borderColor: '#ffffff',
      borderWidth: 2
    }]
  };
  public chartOptions: any = doughnutOptionsBase();

  constructor(
    private resumeService: ResumeService,
    public dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.setResumeData();
    this.resumeService.getDataSubject().subscribe(data => {
      if (!data) return;
      this.dataV2 = data.data;
      this.showGlobal = data.global;
      this.setResumeData();
    });
  }

  public chartClicked(e: any): void {}
  public chartHovered(e: any): void {}

  SetResumeIncomes() {
    this.totalIncomesPaid = Utils.GetValueWithDecimals(this.dataV2.incomesPaid?.[12]?.total || 0);
    this.totalIncomesUnPaid = Utils.GetValueWithDecimals(this.dataV2.incomesUnPaid?.[12]?.total || 0);
    this.setChartsData();
  }

  SetResumeExpenses() {
    this.setChartsData();
  }

  setResumeData() {
    this.setMonths();
    this.SetResumeIncomes();
    this.SetResumeExpenses();
  }

  setMonths() {
    if (this.monthList.length === 0) {
      let i = 1;
      months.forEach(element => {
        const newMonth = new MonthModel();
        newMonth.name = element;
        newMonth.selected = true;
        newMonth.number = i.toString();
        this.monthList.push(newMonth);
        i += 1;
      });
      this.allMonthsSelected = true;
      this.monthsSelected = monthsNumeric;
      ActualSelected.monthSelectedResume = monthsNumeric;
    }
  }

  onChangeMonth(event) {
    this.monthsSelected = event.value;
    ActualSelected.monthSelectedResume = event.value;
    this.allMonthsSelected = false;
    this.monthList.forEach(element => element.selected = false);
    event.value.forEach(element => {
      this.monthList.find(f => f.number == element).selected = true;
    });
    this.updateValues();
  }

  onSelectAll(event) {
    if (event.checked) {
      this.allMonthsSelected = true;
      this.monthsSelected = monthsNumeric;
      ActualSelected.monthSelectedResume = monthsNumeric;
      this.monthControl.setValue(monthsNumeric, { emitEvent: false });
      this.monthList.forEach(element => element.selected = true);
    } else {
      this.allMonthsSelected = false;
      this.monthsSelected = [];
      ActualSelected.monthSelectedResume = [];
      this.monthControl.setValue([], { emitEvent: false });
      this.monthList.forEach(element => element.selected = false);
    }
    this.updateValues();
  }

  updateValues() {
    this.totalIncomesPaid = 0;
    this.totalIncomesUnPaid = 0;

    this.monthsSelected.forEach(element => {
      const i = Number(element) - 1;
      this.totalIncomesPaid += this.dataV2.incomesPaid[i].total;
      this.totalIncomesUnPaid += this.dataV2.incomesUnPaid[i].total;
    });

    this.totalIncomesPaid = Utils.GetValueWithDecimals(this.totalIncomesPaid);
    this.totalIncomesUnPaid = Utils.GetValueWithDecimals(this.totalIncomesUnPaid);

    this.setChartsData();
  }

  setChartsData() {
    this.chartData = {
      labels: ['No cobrado', 'Cobrado'],
      datasets: [{
        data: [this.totalIncomesUnPaid, this.totalIncomesPaid],
        backgroundColor: [chartPalette.warning, chartPalette.good],
        hoverBackgroundColor: [chartPalette.warning, chartPalette.good],
        borderColor: '#ffffff',
        borderWidth: 2
      }]
    };

    const total = this.totalIncomesUnPaid + this.totalIncomesPaid;
    this.chartOptions = doughnutOptionsBase({ text: euro(total), subtext: 'Total', color: chartPalette.textPrimary });
  }

  public percentageOfSalesProfitability(ingresos: number, gasto: number): number {
    const firstResult = ingresos - gasto;
    const secondResult = firstResult / ingresos;
    let finalResult = secondResult * 100;
    finalResult = Utils.GetValueWithDecimals(finalResult);
    return finalResult;
  }

  getSelectedMonthsNames(): string {
    const selectedNames = this.monthsSelected.map(index => this.monthList[parseInt(index) - 1].name);
    return selectedNames.join(', ');
  }

  openDialog(
    ExpensesPaided: boolean = false,
    IncomesPaided: boolean = false,
    ExpensesNoPaided: boolean = false,
    IncomesNoPaided: boolean = false
  ) {
    const ref = this.dialog.open(ResumeDialogComponent, {
      height: '70%',
      width: '70%',
      data: {
        activityUid: ActualSelected.activitySelected,
        expensesPaided: ExpensesPaided,
        expensesNoPaided: ExpensesNoPaided,
        incomesPaided: IncomesPaided,
        incomesNoPaided: IncomesNoPaided,
        showGlobal: this.showGlobal
      },
    });

    ref.afterClosed().subscribe(() => {
      this.resumeService.getResumeIncomes(this.showGlobal, consts.EMPTY_GUID).then(data => {
        this.dataV2.incomesPaid = data.incomesPaid.sort((a, b) => a.numMonth - b.numMonth);
        this.dataV2.incomesUnPaid = data.incomesUnPaid.sort((a, b) => a.numMonth - b.numMonth);
        this.setResumeData();
      });
    });
  }

  openDialogBalance() {
    this.dialog.open(BalanceDialogComponent, {
      data: {
        activityUid: ActualSelected.activitySelected,
        showGlobal: this.showGlobal
      },
    });
  }

  public totalFormated(total: number): number {
    return Utils.GetValueWithDecimals(total);
  }
  public trackByIndex(i: number) { return i; }

}
